import React, { Component, ReactNode } from "react";
import { connect } from "react-redux";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { injectStripe, ReactStripeElements } from "react-stripe-elements";
import get from "lodash.get";

import {
  makePaymentRequest,
  setPaymentStarted,
  makePaymentErrorOccured,
  onValueChange,
  makePaymentIntentPublic,
} from "../reducer";

import { Form, Divider } from "semantic-ui-react";

import { AnalyticsHelper } from "../../../helpers/analyticsHelper";
import { getErrorMessage } from "../../../helpers/donationErrorHelper";

import Card from "./card";
import CoverFee from "./coverFee";
import DonateButton from "./donationButton";
import PaymentRequestForm from "./paymentRequestForm";
import DonationErrorMessage from "./donationErrorMessage";
import { InjectReCaptcha } from "../../../components/common/reCaptcha";

import { ActionNames } from "../../../constants/webTracking";
import { displayNameKeys } from "../../../constants/displayNameOptions";
import { erorrMessages } from "../constants/errorMessages";
import { loadingStatus } from "../../../constants/loading";
import { MAX_DONATION_VALUE } from "../../../constants";
import {
  DONATE_KEY,
  WALLETS,
  DONATION_VIA_WALLET_RECAPTCHA_KEY,
  StripePayment,
} from "./stripePayment";
import { paths } from "../constants";

import DonationService from "../../../services/donationService";
import IdempotencyKeyService from "../../../services/idempotencyKeyService";

import "./index.css";
import { isValidationFormValid } from "../../../helpers/validationHelper";
import { executeWithRecaptcha } from "../../../components/common/reCaptcha";

interface IMakePaymentFormContainerProps extends RouteComponentProps {
  stripe: ReactStripeElements.StripeProps;
  donation: any;
  page: any;
  onValueChange: (path: string, value: any) => void;
  makePayment: (
    donation: any,
    idempotencyKey: string,
    verificationToken: string
  ) => void;
  setPaymentStarted: () => void;
  makePaymentErrorOccured: (error: any) => void;
  makePaymentIntentPublic: (
    guid: string,
    verificationToken: string,
    walletInfoMessage: ReactNode
  ) => void;
}

interface IMakePaymentFormContainerState {
  canMakePayment: boolean;
  paymentRequest: ReturnType<stripe.Stripe["paymentRequest"]>;
  currentDonation?: any;
}

class MakePaymentFormContainer extends Component<
  IMakePaymentFormContainerProps,
  IMakePaymentFormContainerState
> {
  componentWillMount() {
    const disablePayments =
      this.props?.page?.campaign?.template?.value?.customSetup
        ?.standaloneDonationForm?.disableWalletPayments;
    if (disablePayments) return;

    const paymentRequest = this.props.stripe.paymentRequest({
      country: this.props?.page?.campaign?.organization?.country,
      currency:
        this.props?.page?.campaign?.organization?.currency?.toLowerCase(),
      total: {
        label: this.props.donation.amountText,
        amount: parseInt(
          (this.props.donation.paymentRequestAmount * 100).toString()
        ),
      },
    });

    paymentRequest.canMakePayment().then((result) => {
      if (result && (result.applePay || result.googlePay)) {
        this.setState({ canMakePayment: !!result });
      }
    });

    const donation = StripePayment.getDonationForPaymentIntent(
      this.props.donation,
      this.props.page
    );
    // @ts-ignore
    paymentRequest.removeAllListeners("paymentmethod");
    paymentRequest.on("paymentmethod", (ev) =>
      this.paymentMethodCallback(ev, donation)
    );

    this.setState({
      paymentRequest,
      currentDonation: donation,
    });
  }

  paymentMethodCallback = (ev: any, donation: any) => {
    DonationService.makePaymentIntent(
      donation,
      IdempotencyKeyService.getIdempotencyKey()
    ).then((result) => {
      this.props.stripe
        .confirmCardPayment(
          result.data.data.paymentIntentIdentifier,
          { payment_method: ev.paymentMethod.id },
          // @ts-ignore
          { handleActions: false }
        )
        .then((confirmResult: any) => {
          if (confirmResult.error) {
            // Report to the browser that the payment failed, prompting it to
            // re-show the payment interface, or show an error message and close
            // the payment interface.
            ev.complete("fail");

            this.props.makePaymentErrorOccured(confirmResult.error);
          } else {
            // Report to the browser that the confirmation was successful, prompting
            // it to close the browser payment method collection interface.
            ev.complete("success");
            const walletInfoMessage = (
              <React.Fragment>
                Your donation was successfully processed using{" "}
                <strong>
                  {WALLETS.get(ev.walletName)} (
                  {ev.paymentMethod.card.brand.toUpperCase()} ****{" "}
                  {ev.paymentMethod.card.last4})
                </strong>
              </React.Fragment>
            );

            // Check if the PaymentIntent requires any actions and if so let Stripe.js
            // handle the flow. If using an API version older than "2019-02-11"
            // instead check for: `paymentIntent.status === "requires_source_action"`.
            if (confirmResult.paymentIntent.status === "requires_action") {
              // Let Stripe.js handle the rest of the payment flow.
              this.props.stripe
                .confirmCardPayment(result.data.data.paymentIntentIdentifier)
                .then((result: any) => {
                  if (result.error) {
                    // The payment failed -- ask your customer for a new payment method.
                    this.props.makePaymentErrorOccured(result.error);
                  } else {
                    // The payment has succeeded.
                    executeWithRecaptcha(
                      (token: any) =>
                        this.props.makePaymentIntentPublic(
                          result.data.data.identifier,
                          token,
                          walletInfoMessage
                        ),
                      DONATION_VIA_WALLET_RECAPTCHA_KEY
                    );
                  }
                });
            } else {
              // The payment has succeeded.
              executeWithRecaptcha(
                (token: any) =>
                  this.props.makePaymentIntentPublic(
                    result.data.data.identifier,
                    token,
                    walletInfoMessage
                  ),
                DONATION_VIA_WALLET_RECAPTCHA_KEY
              );
            }
          }
        })
        .catch((error) => {
          this.props.makePaymentErrorOccured(error);
        });
    });
  };

  componentDidUpdate(prevProps: IMakePaymentFormContainerProps) {
    if (this.props.donation.paymentInProgress === loadingStatus.SUCCESS) {
      const amount = get(this.props, "donation.createdPaymentRecord.amount");
      const currency = get(
        this.props,
        "donation.createdPaymentRecord.financials.currency"
      );
      const campaignName = get(
        this.props,
        "donation.createdPaymentRecord.campaign.name"
      );
      const paymentId = get(this.props, "donation.createdPaymentRecord.id");

      AnalyticsHelper.createEvent(
        campaignName,
        ActionNames.donation,
        window.location.pathname,
        amount,
        currency
      );
      AnalyticsHelper.executeEcommercePlugin(
        paymentId,
        campaignName,
        amount,
        currency,
        ActionNames.donation,
        null
      );

      if (this.props.match.url.endsWith("/")) {
        this.props.history.push(`${this.props.match.url}thankyou`);
      } else {
        this.props.history.push(`${this.props.match.url}/thankyou`);
      }
    } else {
      const campaignName = get(this.props, "page.campaign.name");
      const donation = StripePayment.getDonationForPaymentIntent(
        this.props.donation,
        this.props.page
      );
      const sameDonation = prevProps.donation === this.props.donation;
      // only update the payment request if the donation has changed
      if (this.state?.paymentRequest && !sameDonation) {
        const amount = this.props.donation?.paymentRequestAmount;
        // if (window) {
        //   AnalyticsHelper.createEvent(
        //     campaignName,
        //     ActionNames.donation,
        //     window.location.pathname,
        //     amount,
        //     ""
        //   );
        // }
        // @ts-ignore
        this.state.paymentRequest.update({
          total: {
            label: this.props.donation.amountText,
            amount: parseInt((amount * 100).toString()),
          },
        });

        this.setState({
          currentDonation: donation,
        });

        // @ts-ignore
        this.state.paymentRequest.removeAllListeners("paymentmethod");
        this.state.paymentRequest.on("paymentmethod", (ev) =>
          this.paymentMethodCallback(ev, donation)
        );
      }
    }
  }

  handleSubmit = (ev: any) => {
    executeWithRecaptcha(
      (token: any) => this.proceedSubmit(ev, token),
      DONATE_KEY
    );
  };

  proceedSubmit = (ev: any, verificationToken = null) => {
    this.props.setPaymentStarted();

    // We don't want to let default form submission happen here, which would refresh the page.
    ev.preventDefault();
    // Within the context of `Elements`, this call to createToken knows which Element to
    // tokenize, since there's only one in this group.

    // TODO: handle case when it fails
    this.props.stripe
      .createToken({
        name: `${this.props.donation.customer.firstName} ${this.props.donation.customer.lastName}`,
        // @ts-ignore
      })
      .then(this.submitPayment.bind(this, verificationToken as any))
      .catch((error) => {
        this.props.makePaymentErrorOccured(error);
      });
  };

  getDisplayName = () => {
    const displayNameOption = get(
      this.props.donation,
      paths.SELECTED_DISPLAY_NAME_OPTION
    );
    return displayNameOption === displayNameKeys.asCustomName
      ? get(this.props, "donation.displayName")
      : null;
  };

  submitPayment = (verificationToken: string, { complete, token }: any) => {
    const donation = StripePayment.getDonation(
      this.props.donation,
      this.props.page,
      token
    );
    this.props.makePayment(
      donation,
      this.props.donation.idempotencyKey,
      verificationToken
    );
    if (complete) {
      complete("success");
    }
  };

  isTooMuchValue = () => {
    return (
      get(this.props, "donation.paymentRequestAmount") > MAX_DONATION_VALUE
    );
  };

  render() {
    const donationAmount = get(this.props, "donation.amount");
    const serverErrorOccured = get(this.props, "donation.serverErrorOccured");
    const serverErrorKey = get(this.props, "donation.serverErrorKey");
    const isTooMuchValue = this.isTooMuchValue();
    const isValid = isValidationFormValid(this.props.donation.tabsValidation);
    const isLoading =
      this.props.donation.paymentInProgress === loadingStatus.IS_LOADING ||
      this.props.donation.fetchingFeeInProgress;

    return (
      <Form onSubmit={this.handleSubmit}>
        {this.state?.canMakePayment && (
          <>
            <Form.Field>
              <PaymentRequestForm
                isDisabled={!isValid || isLoading}
                paymentRequest={this.state.paymentRequest}
                canMakePayment={this.state.canMakePayment}
              />
            </Form.Field>

            <Divider className="switch-payment-method-divider" horizontal>
              or pay with card
            </Divider>
          </>
        )}

        <Form.Field>
          <Card />
        </Form.Field>

        <Form.Field>
          {/* @ts-ignore */}
          <CoverFee amount={donationAmount} />
        </Form.Field>

        <DonateButton disabled={isTooMuchValue} />

        {isTooMuchValue && (
          <DonationErrorMessage
            dismiss={false}
            errorMessage={erorrMessages.MaxValueError}
          />
        )}
        {serverErrorOccured && (
          <DonationErrorMessage
            dismiss={true}
            errorMessage={getErrorMessage(serverErrorKey)}
          />
        )}
      </Form>
    );
  }
}

const mapState = (state: any) => {
  return {
    donation: state.donation,
    page: state.page,
  };
};

const mapDispatch = (dispatch: any) => {
  return {
    onValueChange(path: string, value: any) {
      dispatch(onValueChange(path, value));
    },
    makePayment: (
      donation: any,
      idempotencyKey: string,
      verificationToken: string
    ) => {
      dispatch(makePaymentRequest(donation, idempotencyKey, verificationToken));
    },
    setPaymentStarted: () => {
      dispatch(setPaymentStarted());
    },
    makePaymentIntentPublic: (
      guid: string,
      verificationToken: string,
      walletInfoMessage: string
    ) => {
      dispatch(
        makePaymentIntentPublic(guid, verificationToken, walletInfoMessage)
      );
    },
    makePaymentErrorOccured: (error: any) => {
      dispatch(makePaymentErrorOccured(error));
    },
  };
};

// @ts-ignore
const MakePaymentForm = withRouter(
  connect(
    mapState,
    mapDispatch
  )(InjectReCaptcha(injectStripe(MakePaymentFormContainer as any)))
);
export default MakePaymentForm;
